import React, {useCallback, useState} from 'react';
import classnames from 'classnames';
import axios from "axios";
import { Section, Layout, SEO, StoreBadge } from '..';

import style from './ExperimentPage.module.less';

import clue from './assets/clue.png';

const EXPERIMENT_URL = 'https://us-central1-eleven-puzzles-website.cloudfunctions.net/experiment';
// const EXPERIMENT_URL = 'http://localhost:5002/eleven-puzzles-website/us-central1/experiment';

export const ExperimentPage = () => {
  const [passwordCorrect, setPasswordCorrect] = useState(false);
  const [signUpComplete, setSignUpComplete] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [newsletter, setNewsletter] = useState(false);
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');

  const submit = useCallback(async() => {
    try {
      setLoading(true);

      const response = await axios.post(EXPERIMENT_URL, {
        password
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.data.status === 'ok') {
        setLoading(false);
        setPasswordCorrect(true);
        // scroll to the top
        window.scrollTo(0, 0);
      } else {
        setError('Something went wrong, please try again later');
      }
      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      if (err.response && err.response.data && err.response.status) {
        if (err.response.status === 400 && err.response.data.error === 'Invalid password') {
          setError('Invalid password. Try again, Detective.');
          return;
        }
      }

      setError('Something went wrong, please try again later');
      console.error(err);
    }
  }, [password]);

  const submitEmail = useCallback(async() => {
    try {
      setLoading(true);

      const response = await axios.post(EXPERIMENT_URL, {
        password, email, newsletter
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.data.status === 'ok') {
        setLoading(false);
        setSignUpComplete(true);
        window.scrollTo(0, 0);
      } else {
        setError('Something went wrong, please try again later');
      }
      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      if (err.response && err.response.data && err.response.status) {
        if (err.response.status === 400 && err.response.data.error === 'Invalid email') {
          setError('Invalid email. Please try again, Detective.');
          return;
        }
      }
      setError('Something went wrong, please try again later');
      console.error(err);
    }
  }, [password, email, newsletter]);

  const welcome = (
    <div>
      <h2>Welcome, Detective.</h2>

      <p>You've been invited to the <strong>Parallel Experiment</strong>.</p>

      <p>But there's a catch: what's the password? C.K. left you a clue. Good luck!</p>

      <form
        // action="https://elevenpuzzles.us7.list-manage.com/subscribe/post?u=cc0f56a6940e30d95a3a1a7a0&amp;id=2827db7fbc"
        method="post"
        target="_blank"
        onSubmit={(ev) => {
          ev.preventDefault();
          submit();
        }}
      >
        <div className={style.password}>
          <input
            disabled={loading}
            type="text"
            name="password"
            className={style.emailInput}
            placeholder="Enter password"
            onChange={ev => {
              setPassword(ev.target.value);
              setError('');
            }}
            value={password}
          />
        </div>

        {error ? <div className={style.errorMessage}>{error}</div> : <></>}

        <button type="submit" className={style.subscribe}
                disabled={loading}>{loading ? 'Checking...' : 'Submit'}</button>
      </form>

      <div className={style.image}>
        <a href={clue} target={"_blank"}><img src={clue} alt="Clue" className={style.clue}/></a>
      </div>
    </div>
  );

  const congratulations = (
    <div>
      <h2>Congratulations!</h2>

      <p>Wondering what this is all about? You've earned an answer.</p>

      <p>We're a small video game company, and <strong>on June 5, 2025</strong>, we've released our biggest game yet: <strong>Parallel Experiment</strong>. It's a two-player game where communication is essential to solve puzzles and unravel the mystery of a killer and his experiment.</p>

      <p>You've just earned <strong>two fee keys</strong> to the game. Leave your email address below, and we'll get in touch very soon.</p>

      <form
        action="https://elevenpuzzles.us7.list-manage.com/subscribe/post?u=cc0f56a6940e30d95a3a1a7a0&amp;id=2827db7fbc"
        method="post"
        target="_blank"
        onSubmit={(ev) => {
          ev.preventDefault();
          submitEmail();
        }}
      >
        <div className={style.email}>
          <input
            disabled={loading}
            type="email"
            name="email"
            className={style.emailInput}
            placeholder="Enter your email"
            onChange={ev => {
              setEmail(ev.target.value);
              setError('');
            }}
            value={email}
          />
        </div>

        <div className={style.gdpr}>
          <label className={style.checkbox} htmlFor="gdpr_46661">
            <input type="checkbox" id="gdpr_46661" name="gdpr[46661]" value="Y"
                   onChange={ev => setNewsletter(!!ev.target.checked)} checked={newsletter}/>
            <span>{' '}(optional) I would like to subscribe to the newsletter from Eleven Puzzles.</span>
          </label>
        </div>

        {error ? <div className={style.errorMessage}>{error}</div> : <></>}

        <button type="submit" className={style.subscribe}
                disabled={loading}>{loading ? 'Saving...' : 'Subscribe'}</button>
      </form>
    </div>
  );

  const final = (
    <div>
      <h2>Thank you!</h2>

      <p>We’ll contact you a week or two before the release, which is scheduled for <strong>June 5, 2025</strong>.</p>

      <p>There's only one thing left to do: <strong>wishlist the game on Steam!</strong> Your support means the world to
        us.</p>

      <div className={style.storeBadge}>
        <StoreBadge
          type="Steam Wishlist"
          title="Parallel Experiment"
          link="https://store.steampowered.com/app/2012320/Parallel_Experiment/"
        />
      </div>

      <p>P.S. Can't wait? A <a href="https://store.steampowered.com/app/2775270/Parallel_Experiment_Demo/"
                               target="_blank">free demo</a> of the game is available right now. Go ahead and play!</p>
    </div>
  );

  return (
    <Layout>
      <SEO title="Experiment"
           description="You've been invited to the Parallel Experiment. What's the catch?"/>

      <Section className={classnames(style.section, style.firstSection)}>
        <div className={style.content}>
          <div className={classnames(style.row, style.experiment)}>
            <div className={style.text}>
              {(() => {
                if (signUpComplete) {
                  return final;
                }
                if (passwordCorrect) {
                  return congratulations;
                }
                return welcome;
              })()}
            </div>
          </div>
        </div>
      </Section>
    </Layout>
  );
}

